/**
 * IdentityCard — VERBATIM port from ~/FrontendFinal/Design/src/components/IdentityCard.tsx.
 * Imports nothing, so there is no import to repoint: this file is the design's file, unchanged.
 * The full derivation below is the designer's own and is kept intact — do not condense it. A
 * previous port summarised it, and the lost detail (that 161 assumes THREE pairs) led to the card
 * being given a content height instead of the missing third row of data. The plate is correct;
 * feed it three pairs. See the caller in ../UploadOverlayReskin.tsx.
 *
 * In the reskin the `fields` are the signed-in user's real identity (Name / Department / Job Title)
 * fed from the Vm, which is the shape the TODO(dev) below asks for.
 */
/**
 * IdentityCard
 *
 * The "Your Identity" card in the wizard's left panel. Three label/value pairs
 * on a pale teal plate.
 *
 * MEASURED on `Tool Upload P2.svg`.
 *
 *   card    224, 406   230 x 161   r8   fill #009D9A @ fill-opacity 0.1
 *   text x  236                              (12 inside the card's left edge)
 *
 *   Name:              baseline 424.5     cap-top 416.503
 *   William_Tidmarsh   baseline 444.5     cap-top 436.503
 *   Brand:             baseline 473.5     cap-top 465.503
 *   Volkswagen         baseline 493.5     cap-top 485.503
 *   Job Title:         baseline 522.5     cap-top 514.503
 *   Sales Manager      baseline 542.5     cap-top 534.503
 *
 * THE INTERIOR IS TWO NUMBERS, NOT SIX. Converting those baselines to cap-tops
 * (Inter capHeight = 0.727 x fontSize, so 11px text has its cap 7.997 above the
 * baseline) gives a rhythm that repeats exactly:
 *
 *   card top 406 -> first label cap-top 416.503   = 10.503
 *   label cap-top -> its value cap-top            = 20
 *   value cap-top -> next label cap-top           = 29
 *
 * Closure verified with `derive closure`: 10.503 + 20 + 29 + 20 + 29 + 20 =
 * 128.503, which is the last value's cap-top offset from the card top. It
 * closes, so the three pairs are one flex column — not six placed lines.
 *
 * THOSE THREE NUMBERS ARE NOT THE CSS. They are cap-top-to-cap-top measures,
 * and CSS lays out LINE BOXES. With --leading-body: 20px an 11px cap-top sits
 * 6.003 below its box top, so:
 *
 *   --card-pad-top   10.503 - 6.003 = 4.5    card top -> first label BOX top
 *   label -> value   20 - 20        = 0      the 20 IS the line box: FLUSH
 *   --card-pair-gap  29 - 20        = 9      value box bottom -> next box top
 *
 * The middle one is the trap and the reason there is no --card-row-gap token.
 * Writing `gap-card-row` with the measured 20 would have put a full extra line
 * box between every label and its value, pushing the third pair out of the
 * card's fixed 161px height. The label and its value stack with NO gap; the
 * only gap in this component is the 9 between pairs.
 *
 * This depends on `text-xs` carrying its leading, which tailwind.config.ts
 * enforces by binding xs/sm to --leading-body as a [size, leading] tuple. A
 * bare 11px font-size would make the box 13.312 and silently shift everything.
 *
 * The 0.1 fill is --accent-subtle, the same strength as the inactive step pill
 * and the search bar. It is NOT a separate colour: `fill="#009D9A"` with
 * `fill-opacity="0.1"`. Confusing those two attributes has now caused four real
 * bugs in this project (see CLAUDE.md), which is why the fill is stated as a
 * colour AND a strength everywhere it appears.
 *
 * WHY 161 IS A FIXED HEIGHT. In line-box terms the content occupies
 * 4.5 + 20 + 20 + 9 + 20 + 20 + 9 + 20 + 20 = 142.5 of the card's 161, leaving
 * 18.5 below. That is not a padding value that appears anywhere else in the
 * design, so the card is not "padded" — it is a fixed plate with content
 * top-aligned inside it, which is what the token expresses. `h-identity` with
 * `pt-card-pad-top` reproduces the drawing; a symmetric padding would not.
 *
 * DATA. The three values are the real strings from the frame, and they are the
 * SAME strings the profile pages use ("William_Tidmarsh", "Volkswagen"). They
 * live in the fixture rather than here — see the TODO(dev) on the props.
 */

export interface IdentityField {
  /** Verbatim from the design, colon included. */
  label: string;
  value: string;
}

export interface IdentityCardProps {
  /**
   * TODO(dev): replace with the signed-in user's profile. Shape:
   * `{ label: string; value: string }[]` — three rows on P2, and the design
   * gives no empty/loading state for this card, so it always has all three.
   */
  fields: IdentityField[];
  className?: string;
}

export function IdentityCard({ fields, className = '' }: IdentityCardProps) {
  return (
    <dl
      className={[
        // h-identity, not min-h: see "WHY 161 IS A FIXED HEIGHT" above.
        'flex h-identity w-full min-w-0 flex-col gap-card-pair rounded-md bg-accent-subtle',
        'px-card-x pt-card-pad-top',
        className,
      ].join(' ')}
    >
      {fields.map((field) => (
        // One <div> per pair so the 9px gap falls BETWEEN pairs and never inside
        // one. No gap on this inner column: the label and its value are flush,
        // because the design's 20px label-to-value step is the line box itself.
        <div key={field.label} className="flex min-w-0 flex-col">
          <dt className="text-xs font-medium text-ink">{field.label}</dt>
          <dd className="truncate text-xs font-light text-ink">{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}
